/* In-memory mocked client for Range/Menu — lets pages be previewed with dummy data
   before the gateway/IM backend is reachable locally. Toggle in ./index.ts. */

import { IRangeClient } from './IRangeClient';
import {
  RangeDto,
  MenuDto,
  RangeMenuDto,
  MenuRangeDto,
  MenuRecipeDto,
  RecipeCandidateDto,
  ServicePeriodDto,
  CreateRangeDto,
  UpdateRangeDto,
  CreateMenuDto,
  UpdateMenuDto,
  PagedData,
  ListRangesParams,
  ListMenusParams,
  ListAvailableRecipesParams,
  MenuDisplayStatus,
  MenuStatus,
  MenuType,
  RangeStatsDto,
  MenuStatsDto,
} from './rangeTypes';
import {
  mockRanges,
  mockMenus,
  mockRangeMenus,
  mockMenuRecipes,
  mockRecipeCandidates,
  mockServicePeriods,
} from '../../../../mocks/data/rangeData';

const ranges: RangeDto[] = mockRanges.map((r) => ({ ...r }));
const menus: MenuDto[] = mockMenus.map((m) => ({ ...m }));
const rangeMenuJoins: { rangeId: number; menuId: number }[] = mockRangeMenus.map((j) => ({ ...j }));
const menuRecipeJoins: { menuId: number; recipeId: number; displayOrder: number }[] = mockMenuRecipes.map((j) => ({ ...j }));
const servicePeriods: ServicePeriodDto[] = mockServicePeriods.map((sp) => ({ ...sp }));
const recipeCandidates = mockRecipeCandidates.map((c) => ({ ...c }));

let nextRangeId = Math.max(...ranges.map((r) => r.id)) + 1;
let nextMenuId = Math.max(...menus.map((m) => m.id)) + 1;
let nextServicePeriodId = Math.max(...servicePeriods.map((sp) => sp.id), 0) + 1;
let nextRecipeId = Math.max(...recipeCandidates.map((c) => c.recipeId), 0) + 1;

const delay = <T>(value: T): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(value), 200));

const seededFranchiseIds = new Set<number>(ranges.map((r) => r.franchiseId));

// The fixture data above only covers franchiseIds 1/2. Real demo accounts can have any
// franchiseId, so lazily seed a small demo dataset for whichever franchise is actually
// selected the first time it's requested - otherwise the pages just look permanently empty.
const ensureFranchiseSeeded = (franchiseId: number): void => {
  if (seededFranchiseIds.has(franchiseId)) return;
  seededFranchiseIds.add(franchiseId);

  const breakfastMenu: MenuDto = {
    id: nextMenuId++,
    franchiseId,
    name: 'Breakfast Menu',
    description: 'Morning service items',
    menuType: MenuType.Breakfast,
    status: MenuStatus.Published,
    startDate: null,
    endDate: null,
    displayStatus: MenuDisplayStatus.Active,
    servicePeriodCount: 1,
    recipeCount: 1,
    servicePeriodNames: ['Breakfast'],
    createdBy: 1,
    createdDate: new Date().toISOString(),
    modifiedBy: null,
    modifiedDate: null,
  };
  const lunchMenu: MenuDto = {
    ...breakfastMenu,
    id: nextMenuId++,
    name: 'Lunch Menu',
    description: 'Midday service items',
    menuType: MenuType.Lunch,
    servicePeriodCount: 1,
    recipeCount: 1,
    servicePeriodNames: ['Lunch'],
  };
  menus.push(breakfastMenu, lunchMenu);

  const range: RangeDto = {
    id: nextRangeId++,
    franchiseId,
    name: 'Core Range',
    description: 'Default demo range',
    menuCount: 2,
    createdBy: 1,
    createdDate: new Date().toISOString(),
    modifiedBy: null,
    modifiedDate: null,
  };
  ranges.push(range);
  rangeMenuJoins.push({ rangeId: range.id, menuId: breakfastMenu.id }, { rangeId: range.id, menuId: lunchMenu.id });

  servicePeriods.push(
    { id: nextServicePeriodId++, menuId: breakfastMenu.id, name: 'Breakfast', startTime: '07:00:00', endTime: '11:00:00', daysOfWeek: [0, 1, 2, 3, 4, 5, 6] },
    { id: nextServicePeriodId++, menuId: lunchMenu.id, name: 'Lunch', startTime: '12:00:00', endTime: '14:00:00', daysOfWeek: [1, 2, 3, 4, 5] },
  );

  const recipeOne = { recipeId: nextRecipeId++, recipeName: 'Full English Breakfast', category: 'Breakfast', franchiseId };
  const recipeTwo = { recipeId: nextRecipeId++, recipeName: 'Chicken Caesar Salad', category: 'Main', franchiseId };
  recipeCandidates.push(recipeOne, recipeTwo);
  menuRecipeJoins.push({ menuId: breakfastMenu.id, recipeId: recipeOne.recipeId, displayOrder: 0 });
  menuRecipeJoins.push({ menuId: lunchMenu.id, recipeId: recipeTwo.recipeId, displayOrder: 0 });
};

const computeDisplayStatus = (status: MenuStatus, startDate?: string | null, endDate?: string | null): MenuDisplayStatus => {
  if (status === MenuStatus.Draft) return MenuDisplayStatus.Draft;
  const now = new Date();
  if (startDate && new Date(startDate) > now) return MenuDisplayStatus.Scheduled;
  if (endDate && new Date(endDate) < now) return MenuDisplayStatus.Expired;
  return MenuDisplayStatus.Active;
};

const paginate = <T>(items: T[], page = 1, pageSize = 25): PagedData<T> => {
  const effectivePage = Math.max(page, 1);
  const effectivePageSize = Math.max(pageSize, 1);
  const skip = (effectivePage - 1) * effectivePageSize;
  return {
    totalCount: items.length,
    skip,
    take: effectivePageSize,
    data: items.slice(skip, skip + effectivePageSize),
  };
};

const sortItems = <T>(
  items: T[],
  sortBy: string | undefined,
  sortDirection: 'asc' | 'desc' | undefined,
  getValue: (item: T, key: string) => string | number | null | undefined,
): T[] => {
  const direction = sortDirection === 'desc' ? -1 : 1;
  const key = sortBy || 'name';

  return [...items].sort((left, right) => {
    const leftValue = getValue(left, key);
    const rightValue = getValue(right, key);
    if (leftValue == null && rightValue == null) return 0;
    if (leftValue == null) return -1 * direction;
    if (rightValue == null) return direction;

    const result = typeof leftValue === 'number' && typeof rightValue === 'number'
      ? leftValue - rightValue
      : String(leftValue).localeCompare(String(rightValue));
    return result * direction;
  });
};

const withMenuCounts = (menu: MenuDto): MenuDto => ({
  ...menu,
  servicePeriodCount: servicePeriods.filter((period) => period.menuId === menu.id).length,
  recipeCount: menuRecipeJoins.filter((recipe) => recipe.menuId === menu.id).length,
  servicePeriodNames: servicePeriods
    .filter((period) => period.menuId === menu.id)
    .map((period) => period.name),
});

export class RangeClientMocked implements IRangeClient {
  async getRanges(params: ListRangesParams): Promise<PagedData<RangeDto>> {
    let filtered = ranges
      .map((r) => ({
        ...r,
        menuCount: rangeMenuJoins.filter((join) => join.rangeId === r.id).length,
      }));
    if (params.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter((r) => r.name.toLowerCase().includes(search));
    }
    filtered = sortItems(filtered, params.sortBy, params.sortDirection, (range, key) => {
      switch (key) {
        case 'franchiseId': return range.franchiseId;
        case 'description': return range.description;
        case 'menuCount': return range.menuCount;
        case 'createdDate': return range.createdDate;
        case 'modifiedDate': return range.modifiedDate ?? range.createdDate;
        default: return range.name;
      }
    });
    return delay(paginate(filtered, params.page, params.pageSize));
  }

  async getRangeStats(): Promise<RangeStatsDto> {
    const franchiseRanges = ranges;
    const franchiseMenus = menus;
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const emptyRanges = franchiseRanges.filter((range) => (
      !rangeMenuJoins.some((join) => join.rangeId === range.id)
    ));
    const scheduledOnlyRanges = franchiseRanges.filter((range) => {
      const assignedMenuIds = rangeMenuJoins
        .filter((join) => join.rangeId === range.id)
        .map((join) => join.menuId);
      if (assignedMenuIds.length === 0) return false;
      return assignedMenuIds.every((menuId) => {
        const menu = franchiseMenus.find((item) => item.id === menuId);
        return menu?.displayStatus === MenuDisplayStatus.Scheduled;
      });
    });

    return delay({
      totalRanges: franchiseRanges.length,
      emptyRanges: emptyRanges.length,
      scheduledOnlyRanges: scheduledOnlyRanges.length,
      newRanges: franchiseRanges.filter((range) => new Date(range.createdDate) >= monthStart).length,
    });
  }

  async getRange(id: number): Promise<RangeDto | null> {
    return delay(ranges.find((r) => r.id === id) ?? null);
  }

  async createRange(dto: CreateRangeDto): Promise<RangeDto> {
    const created: RangeDto = {
      id: nextRangeId++,
      franchiseId: dto.franchiseId,
      name: dto.name,
      description: dto.description ?? null,
      menuCount: 0,
      createdBy: 1,
      createdDate: new Date().toISOString(),
      modifiedBy: null,
      modifiedDate: null,
    };
    ranges.push(created);

    const distinctMenuIds = Array.from(new Set(dto.menuIds ?? []));
    distinctMenuIds.forEach((menuId) => rangeMenuJoins.push({ rangeId: created.id, menuId }));
    created.menuCount = distinctMenuIds.length;

    return delay({ ...created });
  }

  async updateRange(id: number, dto: UpdateRangeDto): Promise<RangeDto> {
    const existing = ranges.find((r) => r.id === id);
    if (!existing) throw new Error('Range not found');
    existing.name = dto.name;
    existing.description = dto.description ?? null;

    const distinctMenuIds = Array.from(new Set(dto.menuIds ?? []));
    const remaining = rangeMenuJoins.filter((join) => join.rangeId !== id);
    rangeMenuJoins.length = 0;
    rangeMenuJoins.push(...remaining);
    distinctMenuIds.forEach((menuId) => rangeMenuJoins.push({ rangeId: id, menuId }));

    existing.menuCount = distinctMenuIds.length;
    existing.modifiedBy = 1;
    existing.modifiedDate = new Date().toISOString();
    return delay({ ...existing });
  }

  async deleteRange(id: number): Promise<void> {
    const index = ranges.findIndex((range) => range.id === id);
    if (index < 0) throw new Error('Range not found');
    ranges.splice(index, 1);
    for (let joinIndex = rangeMenuJoins.length - 1; joinIndex >= 0; joinIndex -= 1) {
      if (rangeMenuJoins[joinIndex].rangeId === id) rangeMenuJoins.splice(joinIndex, 1);
    }
    return delay(undefined);
  }

  async getRangeMenus(rangeId: number): Promise<RangeMenuDto[]> {
    const result = rangeMenuJoins
      .filter((j) => j.rangeId === rangeId)
      .map((j) => ({ menuId: j.menuId, menuName: menus.find((m) => m.id === j.menuId)?.name ?? '' }));
    return delay(result);
  }

  async getMenus(params: ListMenusParams): Promise<PagedData<MenuDto>> {
    let filtered = menus
      .map(withMenuCounts);
    if (params.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter((m) => m.name.toLowerCase().includes(search));
    }
    if (params.menuType) {
      filtered = filtered.filter((m) => m.menuType === params.menuType);
    }
    if (params.displayStatus) {
      filtered = filtered.filter((m) => m.displayStatus === params.displayStatus);
    }
    filtered = sortItems(filtered, params.sortBy, params.sortDirection, (menu, key) => {
      switch (key) {
        case 'franchiseId': return menu.franchiseId;
        case 'menuType': return menu.menuType;
        case 'servicePeriodCount': return menu.servicePeriodCount;
        case 'recipeCount': return menu.recipeCount;
        case 'createdDate': return menu.createdDate;
        case 'modifiedDate': return menu.modifiedDate ?? menu.createdDate;
        default: return menu.name;
      }
    });
    return delay(paginate(filtered, params.page, params.pageSize));
  }

  async getMenuStats(): Promise<MenuStatsDto> {
    const franchiseMenus = menus;
    return delay({
      totalMenus: franchiseMenus.length,
      activeMenus: franchiseMenus.filter((menu) => menu.displayStatus === MenuDisplayStatus.Active).length,
      scheduledMenus: franchiseMenus.filter((menu) => menu.displayStatus === MenuDisplayStatus.Scheduled).length,
      draftMenus: franchiseMenus.filter((menu) => menu.displayStatus === MenuDisplayStatus.Draft).length,
    });
  }

  async getMenu(id: number): Promise<MenuDto | null> {
    const menu = menus.find((m) => m.id === id);
    return delay(menu ? withMenuCounts(menu) : null);
  }

  async createMenu(dto: CreateMenuDto): Promise<MenuDto> {
    const created: MenuDto = {
      id: nextMenuId++,
      franchiseId: dto.franchiseId,
      name: dto.name,
      description: dto.description ?? null,
      menuType: dto.menuType ?? null,
      status: dto.status,
      startDate: dto.startDate ?? null,
      endDate: dto.endDate ?? null,
      displayStatus: computeDisplayStatus(dto.status, dto.startDate, dto.endDate),
      servicePeriodCount: 0,
      recipeCount: 0,
      servicePeriodNames: [],
      createdBy: 1,
      createdDate: new Date().toISOString(),
      modifiedBy: null,
      modifiedDate: null,
    };
    menus.push(created);

    const distinctRangeIds = Array.from(new Set(dto.rangeIds ?? []));
    distinctRangeIds.forEach((rangeId) => rangeMenuJoins.push({ rangeId, menuId: created.id }));

    const distinctRecipeIds = Array.from(new Set(dto.recipeIds ?? []));
    distinctRecipeIds.forEach((recipeId, displayOrder) => menuRecipeJoins.push({ menuId: created.id, recipeId, displayOrder }));

    const createdServicePeriods = (dto.servicePeriods ?? []).map((servicePeriod) => ({
      id: nextServicePeriodId++,
      menuId: created.id,
      name: servicePeriod.name,
      startTime: servicePeriod.startTime,
      endTime: servicePeriod.endTime,
      daysOfWeek: servicePeriod.daysOfWeek,
    }));
    servicePeriods.push(...createdServicePeriods);

    return delay(withMenuCounts(created));
  }

  async updateMenu(id: number, dto: UpdateMenuDto): Promise<MenuDto> {
    const existing = menus.find((m) => m.id === id);
    if (!existing) throw new Error('Menu not found');
    existing.name = dto.name;
    existing.description = dto.description ?? null;
    existing.menuType = dto.menuType ?? null;
    existing.status = dto.status;
    existing.startDate = dto.startDate ?? null;
    existing.endDate = dto.endDate ?? null;
    existing.displayStatus = computeDisplayStatus(dto.status, dto.startDate, dto.endDate);

    const distinctRangeIds = Array.from(new Set(dto.rangeIds ?? []));
    const remainingRangeJoins = rangeMenuJoins.filter((join) => join.menuId !== id);
    rangeMenuJoins.length = 0;
    rangeMenuJoins.push(...remainingRangeJoins);
    distinctRangeIds.forEach((rangeId) => rangeMenuJoins.push({ rangeId, menuId: id }));

    const distinctRecipeIds = Array.from(new Set(dto.recipeIds ?? []));
    const remainingRecipeJoins = menuRecipeJoins.filter((join) => join.menuId !== id);
    menuRecipeJoins.length = 0;
    menuRecipeJoins.push(...remainingRecipeJoins);
    distinctRecipeIds.forEach((recipeId, displayOrder) => menuRecipeJoins.push({ menuId: id, recipeId, displayOrder }));

    const remainingServicePeriods = servicePeriods.filter((servicePeriod) => servicePeriod.menuId !== id);
    servicePeriods.length = 0;
    servicePeriods.push(...remainingServicePeriods);
    const replacementServicePeriods = (dto.servicePeriods ?? []).map((servicePeriod) => ({
      id: nextServicePeriodId++,
      menuId: id,
      name: servicePeriod.name,
      startTime: servicePeriod.startTime,
      endTime: servicePeriod.endTime,
      daysOfWeek: servicePeriod.daysOfWeek,
    }));
    servicePeriods.push(...replacementServicePeriods);

    existing.modifiedBy = 1;
    existing.modifiedDate = new Date().toISOString();
    return delay(withMenuCounts(existing));
  }

  async deleteMenu(id: number): Promise<void> {
    const index = menus.findIndex((menu) => menu.id === id);
    if (index < 0) throw new Error('Menu not found');
    menus.splice(index, 1);
    for (let joinIndex = rangeMenuJoins.length - 1; joinIndex >= 0; joinIndex -= 1) {
      if (rangeMenuJoins[joinIndex].menuId === id) rangeMenuJoins.splice(joinIndex, 1);
    }
    return delay(undefined);
  }

  async getMenuRanges(menuId: number): Promise<MenuRangeDto[]> {
    const result = rangeMenuJoins
      .filter((j) => j.menuId === menuId)
      .map((j) => ({ rangeId: j.rangeId, rangeName: ranges.find((r) => r.id === j.rangeId)?.name ?? '' }));
    return delay(result);
  }

  async getMenuRecipes(menuId: number): Promise<MenuRecipeDto[]> {
    const result = menuRecipeJoins
      .filter((j) => j.menuId === menuId)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((j) => {
        const candidate = recipeCandidates.find((c) => c.recipeId === j.recipeId);
        return {
          recipeId: j.recipeId,
          recipeName: candidate?.recipeName ?? `Recipe #${j.recipeId}`,
          category: candidate?.category ?? null,
          displayOrder: j.displayOrder,
        };
      });
    return delay(result);
  }

  async getAvailableRecipes(params: ListAvailableRecipesParams): Promise<PagedData<RecipeCandidateDto>> {
    ensureFranchiseSeeded(params.franchiseId);
    let filtered = recipeCandidates.filter((c) => c.franchiseId === params.franchiseId);
    if (params.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter((c) => c.recipeName.toLowerCase().includes(search));
    }
    const sorted = [...filtered]
      .sort((a, b) => a.recipeName.localeCompare(b.recipeName))
      .map((c) => ({ recipeId: c.recipeId, recipeName: c.recipeName, category: c.category }));
    return delay(paginate(sorted, params.page, params.pageSize));
  }

  async getServicePeriods(menuId: number): Promise<ServicePeriodDto[]> {
    return delay(servicePeriods.filter((sp) => sp.menuId === menuId).map((sp) => ({ ...sp })));
  }

}
